import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getProduct } from "../actions/getProduct";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import Accordion from "../components/accordion";
import LoadingScreen from "../components/loading-screen";
import TopSection from "../components/product/TopSection";
import SubProductsSection from "../components/product/SubProductsSection";
import Attributes from "../components/product/Attributes";
import BottomSection from "../components/product/BottomSection";
import { Helmet } from "react-helmet-async";
import { getCurrency } from "../lib/middleware";
import TamaraSection from "../components/product/TamaraSection";
import TabbyWidget from "../components/product/TabbyWidget";

export default function Product() {
    const { productId } = useParams();
    const { i18n, t } = useTranslation();
    const currentLang = i18n.language.startsWith("ar") ? "ar" : "en";

    const { data, isLoading } = useQuery({
        queryKey: ["global", "product", { productId }],
        queryFn: () =>
            getProduct({
                productId: productId || "",
            }),
    });

    const [subProductID, setSubProductID] = useState(0);
    const [cartAttributes, setCartAttributes] = useState([]);

    const [wantCook, setWantCook] = useState(false);
    const currency = getCurrency();

    useEffect(() => {
        if (!data) return;
        const currency = getCurrency();
        const value = Number(data.price) || 0;

        // --- Meta Pixel
        if (typeof fbq !== "undefined") {
            fbq("track", "ViewContent", {
                content_name: data.name,
                content_ids: [data.id],
                content_type: "product",
                value,
                currency,
            });
        }

        // --- Snap Pixel
        if (typeof snaptr !== "undefined") {
            snaptr("track", "VIEW_CONTENT", {
                item_ids: [data.id],
                price: value,
                currency,
            });
        }

        // --- TikTok Pixel (safe async load)
        if (window.ttq) {
            window.ttq.track("ViewContent", {
                content_name: data.name,
                content_ids: [String(data.id)],
                content_type: "product",
                value: Number(data.price),
                currency: "AED",
            });
        }
    }, [data]);

    if (isLoading) return <LoadingScreen />;
    if (!data) return null;

    return (
        <>
            <Helmet>
                <title>{data.name} - Zabehaty</title>
                <meta name="description" content={data.description} />
                <meta
                    name="keywords"
                    content={`${data?.name}, ${data?.shop?.name}, ${data?.category?.name}, Zabehaty, Zabehaty Product`}
                />

                {/* Open Graph for Facebook/Snapchat */}
                <meta property="og:title" content={`${data.name} - Zabehaty`} />
                <meta property="og:description" content={data.description} />
                <meta property="og:image" content={data.image} />
                <meta
                    property="og:url"
                    content={`https://www.zabehaty.com/product/${data.id}`}
                />
                <meta property="og:type" content="product" />
            </Helmet>

            <div className="mx-auto flex max-w-[852px] flex-col px-4 pt-14">
                {/* title,description and image */}
                <TopSection data={data} />
                <TamaraSection data={data} />
                <TabbyWidget
                    key={`tabby-${currentLang}-${data.price}`}
                    price={data.price}
                    currency={currency}
                    lang={currentLang}
                />
                <div className="flex flex-col gap-y-4">
                    {data.sub_product && data.sub_product?.length > 0 && (
                        <SubProductsSection
                            data={data}
                            setSubProductID={setSubProductID}
                            subProductID={subProductID}
                        />
                    )}

                    <Attributes
                        data={data}
                        cartAttributes={cartAttributes}
                        setCartAttributes={setCartAttributes}
                    />
                </div>
            </div>

            {/* quantity, price and add to cart  */}
            <BottomSection
                data={data}
                cartAttributes={cartAttributes}
                subProductID={subProductID}
            />
        </>
    );
}
